import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
// import { useAuth0 } from "@auth0/auth0-react";
import { uniq, get } from "lodash";
import clsx from "clsx";
// import useCustomLoginWithRedirect from "../../../../../hooks/useCustomLoginWithRedirect";

import { PropertyDetails } from "./PropertyDetails";
import SocialPropertySwiper from "./SocialPropertySwiper";
import GuessPriceModal from "../GuessPriceModal";
import CommentsModal from "../CommentsModal/CommentsModal";
import Comment from "../CommentsModal/Comment";
import CustomTooltip from "../../../../../components/common/CustomTooltip";

// import { FaArrowDown, FaArrowUp } from "react-icons/fa";
import { PiShareFatFill } from "react-icons/pi";
import { MdComment } from "react-icons/md";

import useLocalStorage from "../../../../../hooks/useLocalStorage";
import config from "../../../../../config";
import { triggerShare } from "../../../../../helpers/triggerShare";
import {
    createGuess /* , deleteUserSocialGuess */,
    getUserComments,
} from "../../../../../services/UserService";
import { useUser } from "../../../../../context/UserContext";
// import { GUESS_TYPES } from "../../../../../constants/guess-types";
import TagList from "../../../../../components/common/TagList";
import HashtagModal from "../HashtagModal";
import { formatPrice } from "../../../../../helpers/helpers";
import { getDisplayPrice } from "../../../../../helpers/helpers";
import useMixpanelEvent from "../../../../../hooks/useMixpanelEvent";
import useMixpanelPageViewTracking from "../../../../../hooks/useMixpanelPageViewTracking";
import { FIVE_MINUTES, THIRTY_MINUTES } from "../../../../../constants/time";

const ITEMS_PER_PAGE = 10;
const STALE_TIME = FIVE_MINUTES; // 5 minutes
const CACHE_TIME = THIRTY_MINUTES; // 30 minutes

/**
 * SinglePropertyFeed component displays Interaction Button with Property Details and Images  on Listing Feed Page
 * @param {Object} props - Component props
 * @param {Object} props.property - Properties Details
 * @param {string} props.feedType - Type of feed (trending or following)
 * @returns {JSX.Element} - Single Property with Interaction Button.
 */
const SinglePropertyFeed = ({ property, feedType = "trending", onCardClick }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userState } = useUser();
    // const { isAuthenticated } = useAuth0();
    const userId = userState?.id;
    const [socialState, setSocialState] = useLocalStorage("social-state");
    const [guessedSocialProperties, setGuessedSocialProperties] = useLocalStorage(
        "guessed-social-properties",
        []
    );
    // const { customLoginWithRedirect } = useCustomLoginWithRedirect();
    const currentUserGuess = get(property, "currentUserGuess.0.prediction", null);
    const [guessType, setGuessType] = useState(null);
    const [showGuessPriceModal, setShowGuessPriceModal] = useState(false);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [preferredVersion, setPreferredVersion] = useLocalStorage("live-variant-preferred-version");
    const isSocial = window.location.pathname.includes("/social");
    const [showHashtagModal, setShowHashtagModal] = useState(false);
    const [showTagCommentsModal, setShowTagCommentsModal] = useState(false);
    const [selectedTag, setSelectedTag] = useState(null);
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [visitorStatus] = useLocalStorage("visitor-status");
    const propertyRef = useRef(null);
    const [showCommentTooltip, setShowCommentTooltip] = useState(false);

    // Use the custom hook for page view tracking
    useMixpanelPageViewTracking({
        targetRef: propertyRef,
        property,
        pageName: `Property Feed - ${feedType.charAt(0).toUpperCase() + feedType.slice(1)}`,
        userState,
        visitorStatus,
        feedType,
        threshold: 1.0,
        debounceDelay: 100,
    });

    // Update guessType when currentUserGuess changes
    useEffect(() => {
        if (currentUserGuess) {
            setGuessType(currentUserGuess);
        }
    }, [currentUserGuess]);

    const { mutate: submitGuess } = useMutation({
        mutationFn: ({ userId, propertyId, prediction, guessPrice, guessReason }) =>
            createGuess(userId, propertyId, prediction, guessPrice, guessReason),
        onSuccess: () => {
            setGuessType(null);
            if (!guessedSocialProperties.includes(property.id)) {
                setGuessedSocialProperties(uniq([...guessedSocialProperties, property.id]));
            }
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingFeed-properties"],
            });
            queryClient.invalidateQueries(["comments"]);
            toast.success("Guess submitted successfully!");
        },
        onError: (error) => {
            console.error("Error submitting guess:", error);
            toast.error(error.response?.data?.message || "Failed to submit guess");
        },
    });

    /* Guess related. Commented out for now */
    /* const { mutate: deleteGuess } = useMutation({
        mutationFn: () => deleteUserSocialGuess(userId, property.id),
        onSuccess: () => {
            setGuessType(null);
            // Optimistically update all related caches
            queryClient.setQueryData(["user-guess", property.id, userId], (oldData) => ({
                ...oldData,
                guess: [],
            }));

            // Update listing feed cache for infinite query
            queryClient.setQueriesData(["live-variant-listingFeed-properties"], (oldData) => {
                if (!oldData?.pages) return oldData;
                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => ({
                        ...page,
                        data: {
                            ...page.data,
                            data: page.data.data.map((prop) => {
                                if (prop.id === property.id) {
                                    return {
                                        ...prop,
                                        currentUserGuess: [],
                                    };
                                }
                                return prop;
                            }),
                        },
                    })),
                };
            });

            // Update listing page cache
            queryClient.setQueryData(["live-variant-listingPage-property", property.id], (oldData) => {
                if (!oldData?.data) return oldData;
                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        currentUserGuess: [],
                    },
                };
            });

            // Invalidate all related queries
            queryClient.invalidateQueries({
                queryKey: ["live-variant-listingFeed-properties"],
            });
            queryClient.invalidateQueries(["comments"]);
            queryClient.invalidateQueries(["userProperties"]);
            queryClient.invalidateQueries(["user-guess"]);
            queryClient.invalidateQueries(["listing-page-user-guess"]);
            toast.success("Guess deleted successfully!");
        },
        onError: (error) => {
            console.error("Error deleting guess:", error);
            toast.error(error.response?.data?.message || "Failed to delete guess");
        },
    }); */

    /* Guess related. Commented out for now */
    /* const handleGuessClick = (type) => {
        // If clicking the same guess type, delete the guess

        if (!isAuthenticated) {
            customLoginWithRedirect({
                authorizationParams: {
                    redirect_uri: window.location.origin + "/social",
                },
                appState: {
                    toggleProfile: true,
                    event: {
                        name: "tutorial_register_click",
                        referral_code: localStorage.getItem("referral_code"),
                    },
                },
            });
            return;
        }

        if (type === guessType) {
            deleteGuess();
            return;
        }

        setSocialState({
            ...socialState,
            property,
            guess: {
                prediction: type,
                guessPrice: type === GUESS_TYPES.EXACT ? Number(property.list_price) : 0,
                guessReason: "",
            },
        });
        setGuessType(type);
        setShowCommentModal(true);
    }; */

    const { data: commentsData } = useQuery({
        queryKey: ["userComments", userId],
        queryFn: ({ pageParam = 1 }) => getUserComments(userId, pageParam, ITEMS_PER_PAGE),
        enabled: !!userId,
        staleTime: STALE_TIME, // Consider data fresh for 5 minutes
        cacheTime: CACHE_TIME, // Keep data in cache for 30 minutes
    });

    const handleSubmitGuess = (NewGuessPriceAndReason) => {
        setShowGuessPriceModal(false);

        submitGuess({
            userId,
            propertyId: property.id,
            prediction: guessType,
            guessPrice: Number(NewGuessPriceAndReason.price),
            guessReason: NewGuessPriceAndReason.reason,
        });
    };

    const handleUserClick = () => {
        if (onCardClick) onCardClick();
        setSocialState({
            ...socialState,
            property: property,
            lastScrollPosition: window.scrollY,
        });
        navigate(
            `/social/listing/${encodeURIComponent(property.full_address.replace(/\s+/g, "-"))}/${encodeURIComponent(property.property_link_code)}`
        );
    };

    const handleShare = async () => {
        if (!property) return;

        setSocialState({
            ...socialState,
            shareProperty: property.id,
        });

        const shareTextTitle = `PriceMe Social`;
        const shareTextBody = `${property.full_address}`;
        const shareUrl = `${config.BASE_URL}/${preferredVersion}/listing/${encodeURIComponent(property.full_address.replace(/\s+/g, "-"))}/${encodeURIComponent(property.property_link_code)}?utm_medium=share`;

        // Track share event
        const eventData = {
            isAuthenticated: !!userState?.id,
            visitorStatus: visitorStatus || "new",
            pageName: "Property Feed",
            propertyUrl: shareUrl,
            user_id: userState?.id || null,
            nickname: userState?.nickname || null,
            email: userState?.email || null,
        };

        sendMixpanelEvent("Share", eventData);

        await triggerShare({
            web: {
                text: `${shareTextTitle}\n${shareTextBody}\n\n${shareUrl}`,
            },
            mobile: {
                title: shareTextTitle,
                text: shareTextBody,
                url: shareUrl,
            },
        });
    };

    const handleCommentPreviewClick = () => {
        setSocialState({
            ...socialState,
            property,
            commentId: property.previewComment.id,
        });
        setShowCommentModal(true);
    };

    const handleCloseCommentsModal = () => {
        setShowCommentModal(false);
        setGuessType(currentUserGuess);
        setSocialState({
            ...socialState,
            property,
            guess: null,
            commentId: null,
        });
        setSelectedTag(null);
    };

    const handleTagClick = (tag) => {
        setSelectedTag(tag);
        setShowTagCommentsModal(true);
    };

    useEffect(() => {
        if (isSocial) {
            setPreferredVersion("social");
        }
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (commentsData?.total === 0) {
            setShowCommentTooltip(true);
        }
    }, [commentsData?.total]);

    return (
        <>
            <div
                ref={propertyRef}
                className="tw-py-3 tw-px-3 bg-white rounded-lg shadow-md tw-border-b-[1px] tw-border-gray-400"
            >
                {/* New Header Section */}
                <div onClick={handleUserClick}>
                    <div className="tw-cursor-pointer">
                        <div className="tw-text-2xl tw-font-bold tw-text-black tw-leading-tight tw-font-merriweather">
                            {formatPrice(getDisplayPrice(property))}
                        </div>
                        <div className="tw-text-base tw-text-gray-500 tw-mt-1 tw-mb-2">
                            {property.street_address} | {property.neighborhood}
                        </div>
                    </div>

                    {/* Slider and propertyDetails Section */}
                    <div className="tw-relative " data-tooltip-id="see-more-tooltip">
                        <SocialPropertySwiper property={property} />
                        <div className="tw-absolute tw-bottom-0 tw-z-10 tw-bg-black/25 tw-w-full !tw-rounded-b-lg">
                            <PropertyDetails
                                beds={property.bedrooms}
                                baths={property.bathrooms}
                                sqFt={property.square_footage}
                                type={property.property_type}
                            />
                        </div>
                    </div>
                </div>

                {/* Guessing & Actions Section */}
                <div className="tw-flex tw-items-center tw-justify-start tw-mt-2 tw-text-gray-700 tw-gap-x-3">
                    {/* Price Guessing Buttons: Commented out for now */}
                    {/* <div
                        className={clsx("tw-flex tw-items-center tw-rounded-full tw-bg-gray-300 tw-p-1", {
                            "tw-opacity-60 tw-pointer-events-none": property?.status_indicator === "Closed",
                            "!tw-bg-[#13C5FC]": guessType === GUESS_TYPES.EXACT,
                        })}
                    >
                        <div
                            className={clsx(
                                "tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-cursor-pointer",
                                {
                                    "tw-bg-[#7CC836]": guessType === GUESS_TYPES.HIGHER,
                                }
                            )}
                            onClick={() => handleGuessClick(GUESS_TYPES.HIGHER)}
                        >
                            <FaArrowUp />
                        </div>
                        <span
                            onClick={() => handleGuessClick(GUESS_TYPES.EXACT)}
                            className="tw-font-bold tw-cursor-pointer tw-px-2 tw-py-0.5"
                        >
                            ${property?.list_price?.toLocaleString("en-US")}
                        </span>
                        <div
                            className={clsx(
                                "tw-w-7 tw-h-7 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-cursor-pointer",
                                {
                                    "tw-bg-[#EA4949]": guessType === GUESS_TYPES.LOWER,
                                }
                            )}
                            onClick={() => handleGuessClick(GUESS_TYPES.LOWER)}
                        >
                            <FaArrowDown />
                        </div>
                    </div> */}
                    {/* Comments, Share, AddTag Row */}
                    <div className="tw-flex tw-items-center tw-gap-4 tw-mt-2">
                        <button
                            className={clsx(
                                "tw-flex tw-items-center tw-bg-[#F3F4F6] tw-border tw-text-black tw-border-gray-300 tw-rounded-full tw-px-3 tw-py-2 tw-shadow-sm hover:tw-bg-gray-100 tw-transition-colors",
                                commentsData?.total === 0 && "pulse"
                            )}
                            onClick={() => setShowCommentModal(true)}
                            data-tooltip-id="comment-tooltip"
                        >
                            <MdComment className="tw-mr-2 tw-text-lg" />
                            <span className="tw-font-semibold tw-text-sm">
                                {property.commentCount} Comments
                            </span>
                        </button>
                        {commentsData?.total === 0 && (
                            <CustomTooltip
                                id="comment-tooltip"
                                open={showCommentTooltip}
                                style={{ "--tooltip-bg": "#6C26C4" }}
                                onClose={() => setShowCommentTooltip(false)}
                                placement="bottom"
                                animate
                                autoCloseTime={2500}
                                customContent={
                                    <span>
                                        <b>Comment:</b> Share a hot take or ask a question.
                                    </span>
                                }
                            />
                        )}
                        <button
                            className="tw-flex tw-items-center tw-bg-[#F3F4F6] tw-border tw-border-gray-300 tw-text-black tw-rounded-full tw-px-3 tw-py-2 tw-shadow-sm hover:tw-bg-gray-100 tw-transition-colors"
                            onClick={handleShare}
                        >
                            <PiShareFatFill className="tw-mr-2 tw-text-lg tw-font-black" />
                            <span className="tw-font-semibold tw-text-sm">Share</span>
                        </button>
                        <button
                            className="tw-bg-[#6C26C4] tw-text-white tw-font-semibold tw-px-4 tw-py-2.5 tw-rounded-full tw-text-xs tw-shadow-md hover:tw-bg-[#4b2fcf] tw-transition-colors"
                            style={{ minWidth: "fit-content" }}
                            onClick={() => setShowHashtagModal(true)}
                        >
                            #AddTag
                        </button>
                    </div>
                </div>
                {/* Guess related. Commented out for now */}
                {/* <p className="tw-text-sm pt-2">{property.guessCount} people guessed</p> */}

                {/* TagList below actions */}
                <TagList
                    tags={property.propertyTags}
                    onTagClick={handleTagClick}
                    selectedTag={selectedTag}
                    propertyId={property.id}
                />

                {/* Comment Preview Section */}
                {property?.previewComment && (
                    <div
                        className="tw-border-t tw-border-gray-200 tw-mt-2 tw-cursor-pointer"
                        onClick={handleCommentPreviewClick}
                    >
                        <Comment
                            comment={property.previewComment}
                            maxLines={2}
                            showSeeMore={false}
                            enableReplies={false}
                            enableNestedReplies={false}
                            showReplyButton={false}
                            showViewMoreButton={false}
                        />
                    </div>
                )}
            </div>

            {/* Guess Price Modal */}
            {showGuessPriceModal && (
                <GuessPriceModal
                    guess={guessType}
                    show={showGuessPriceModal}
                    handleClose={() => setShowGuessPriceModal(false)}
                    onSubmit={handleSubmitGuess}
                    list_price={getDisplayPrice(property)}
                />
            )}

            {showCommentModal && (
                <CommentsModal
                    propertyId={property.id}
                    show={showCommentModal}
                    handleClose={handleCloseCommentsModal}
                    tags={property.propertyTags}
                />
            )}
            <HashtagModal
                userId={userId}
                propertyId={property.id}
                show={showHashtagModal}
                onClose={() => setShowHashtagModal(false)}
            />
            {showTagCommentsModal && selectedTag && (
                <CommentsModal
                    propertyId={property.id}
                    show={showTagCommentsModal}
                    handleClose={() => {
                        setShowTagCommentsModal(false);
                        setSelectedTag(null);
                    }}
                    tag={selectedTag}
                    tags={property.propertyTags}
                />
            )}
        </>
    );
};

export default SinglePropertyFeed;

SinglePropertyFeed.propTypes = {
    property: PropTypes.object.isRequired,
    feedType: PropTypes.oneOf(["trending", "following"]).isRequired,
    onCardClick: PropTypes.func,
};
